<template>
  <el-dialog
    title="按类目配置产品参数图片"
    :visible.sync="dialogVisible"
    width="900px"
    :close-on-click-modal="false"
    @close="handleClose"
    class="product-param-image-dialog"
  >
    <div class="search-section" style="margin-bottom: 10px; display: flex; align-items: center; gap: 8px;">
      <el-input
        v-model="searchKeyword"
        placeholder="输入关键词查询类目"
        style="width: 200px;"
        clearable
        @keyup.enter.native="handleSearch"
      />
      <el-button size="mini" type="primary" @click="handleSearch">查询</el-button>
    </div>
    <div>
      <el-button size="mini" type="primary" @click="handleAddRow" style="margin-bottom: 10px;">添加配置</el-button>
    </div>
    <div class="table-container">
      <el-table :data="tableData" border class="config-table" max-height="580">
      <el-table-column prop="categoryPath" label="适用类目" width="300">
        <template slot-scope="{ row }">
          <span v-if="row.categoryId">{{ row.categoryNames }}</span>
          <el-link v-else type="primary" @click="handleSelectCategory(row)">选择类目</el-link>
        </template>
      </el-table-column>
      <el-table-column prop="paramImage" label="产品参数图片" width="200" class-name="image-cell">
        <template slot-scope="{ row }">
          <div class="image-cell-content">
            <ImgUpload
              :key="row.paramImageUrl"
              :file="row.paramImageUrl"
              :is-single="true"
              :width="80"
              :height="80"
              @upload-success="(url) => handleUploadSuccess(url, row)"
              @del="handleImageDelete(row)"
            />
            <div v-if="rowsMissingImage.has(row._uid)" class="image-error-tip">请上传产品参数图片</div>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="operate" label="操作" width="200">
        <template slot-scope="{ row, $index }">
          <el-button
            size="mini"
            type="link"
            style="color: red;"
            @click="handleDeleteRow($index, row)"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    </div>

    <div class="pagination-wrapper">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagination.pageNum"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagination.pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="pagination.total"
      />
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" :loading="submitLoading" @click="handleSubmit">确认</el-button>
    </span>

    <!-- 类目选择弹窗 -->
    <CascaderDialog ref="categoryDialog" platform="tm" :shopId="shopId" />
  </el-dialog>
</template>

<script>
import { queryCategoryImageConfig, saveOrUpdateCategoryImageConfig, deleteCategoryImageConfig } from '@/api/prod/conf/index.js';
import CascaderDialog from '@/components/prod/center/components/PlatformCategoryInput/CascaderDialog.vue';
import ImgUpload from '@/components/Uploads/ImgUpload.vue';

export default {
  name: 'ProductParamImageDialog',
  components: { CascaderDialog, ImgUpload },
  props: {
    visible: { type: Boolean, default: false },
  },
  data() {
    return {
      tableData: [],
      originalTableData: [],
      searchKeyword: '',
      submitLoading: false,
      currentEditRow: null,
      shopId: '',
      shopType: 'tm', // 天猫平台
      rowsMissingImage: new Set(), // 未上传图片的行 uid
      uidCounter: 0, // 生成唯一 uid
      pagination: {
        pageNum: 1,
        pageSize: 10,
        total: 0,
      },
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(v) {
        this.$emit('update:visible', v);
      },
    },
  },
  watch: {
    visible(v) {
      if (v) {
        this.fetchData();
      }
    },
  },
  created() {
    const tmShops = this.$store.getters.get_AllShops_BySource?.('tm') || [];
    this.shopId = tmShops[0]?.shopId || '';
  },
  methods: {
    async fetchData() {
      this.submitLoading = true;
      this.rowsMissingImage.clear();
      try {
        const res = await queryCategoryImageConfig({
          shopType: this.shopType,
          pageNum: this.pagination.pageNum,
          pageSize: this.pagination.pageSize,
          keyword: this.searchKeyword || undefined,
        });
        if (res.result === 1 && res.data) {
          const records = res.data.records || res.data.list || res.data || [];
          const total = res.data.total || res.data.count || 0;

          this.pagination.total = total;
          this.originalTableData = records.map(item => ({
            _uid: ++this.uidCounter,
            id: item.id,
            categoryId: item.categoryId,
            categoryNames: item.categoryNames,
            paramImageUrl: item.imageUrl,
          }));
          // 深拷贝，避免修改 tableData 时影响 originalTableData
          this.tableData = JSON.parse(JSON.stringify(this.originalTableData));
        } else {
          this.tableData = [];
          this.originalTableData = [];
          this.pagination.total = 0;
        }
      } catch (e) {
        console.error('fetchData error', e);
        this.$message.error('获取数据失败');
      } finally {
        this.submitLoading = false;
      }
    },
    handleSearch() {
      this.pagination.pageNum = 1;
      this.fetchData();
    },
    handleSizeChange(val) {
      this.pagination.pageSize = val;
      this.pagination.pageNum = 1;
      this.fetchData();
    },
    handleCurrentChange(val) {
      this.pagination.pageNum = val;
      this.fetchData();
    },
    handleSelectCategory(row) {
      this.currentEditRow = row;
      const categoryIds = row.categoryId ? [row.categoryId] : [];
      this.$refs.categoryDialog.method_open(categoryIds, ([ids, labels]) => {
        if (this.currentEditRow && ids && ids.length > 0) {
          this.currentEditRow.categoryId = ids[ids.length - 1];
          this.currentEditRow.categoryNames = labels ? labels.join(' > ') : '';
        }
        this.currentEditRow = null;
      });
    },
    handleUploadSuccess(url, row) {
      row.paramImageUrl = url;
      this.rowsMissingImage.delete(row._uid);
      this.$message.success('上传成功');
    },
    handleImageDelete(row) {
      row.paramImageUrl = null;
      this.rowsMissingImage.add(row._uid);
    },
    handleAddRow() {
      this.tableData.unshift({
        _uid: ++this.uidCounter,
        id: null,
        categoryId: null,
        categoryNames: null,
        paramImageUrl: null,
        paramImageId: null,
      });
    },
    handleDeleteRow(index, row) {
      if (row.id) {
        // 已保存的数据需要调用删除接口
        this.$confirm('确认要删除该配置吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        }).then(async () => {
          try {
            const res = await deleteCategoryImageConfig({ id: row.id });
            if (res.result === 1) {
              this.tableData.splice(index, 1);
              this.$message.success('删除成功');
              this.$emit('success', this.tableData.length);
            }
          } catch (e) {
            console.error('delete error', e);
            this.$message.error('删除失败');
          }
        }).catch(() => {});
      } else {
        // 未保存的数据直接删除
        this.tableData.splice(index, 1);
      }
    },
    async handleSubmit() {
      // 过滤有效数据（有类目的）
      const validData = this.tableData.filter(item => item.categoryId !== null && item.categoryId !== undefined);

      if (validData.length === 0) {
        this.$message.warning('请至少配置一条类目');
        return;
      }

      // 检查有类目但未上传图片的行
      const missingRows = this.tableData.filter(item => item.categoryId && !item.paramImageUrl);
      if (missingRows.length > 0) {
        this.rowsMissingImage = new Set(missingRows.map(r => r._uid));
        this.$message.warning('请为所有类目上传产品参数图片');
        return;
      }

      this.submitLoading = true;
      try {
        // 构建原始数据的 Map，key 为 id
        const originalDataMap = new Map();
        this.originalTableData.forEach(item => {
          if (item.id) {
            originalDataMap.set(item.id, item);
          }
        });

        // 转换为接口需要的格式：只传图片有改动的已有数据和新增数据
        const list = validData
          .filter(item => item.paramImageUrl) // 只传有图片的配置
          .map(item => {
            const originalItem = item.id ? originalDataMap.get(item.id) : null;
            // 如果是已有数据，检查图片是否有改动
            if (item.id && originalItem) {
              // 图片没有改动，不传
              if (item.paramImageUrl === originalItem.paramImageUrl) {
                return null;
              }
            }
            // 新增数据（无 id）或图片有改动的已有数据
            return {
              id: item.id || undefined, // 有 ID 表示编辑，无 ID 表示新增
              shopType: 'tm', // 天猫平台
              categoryId: item.categoryId,
              categoryNames: item.categoryNames,
              imageUrl: item.paramImageUrl,
            };
          })
          .filter(item => item !== null); // 过滤掉不需要更新的数据

        const res = await saveOrUpdateCategoryImageConfig(list);
        if (res.result === 1) {
          this.$message.success('保存成功');
          this.$emit('success');
          this.handleClose();
        }
      } catch (e) {
        console.error('save error', e);
        this.$message.error('保存失败');
      } finally {
        this.submitLoading = false;
      }
    },
    handleClose() {
      this.$emit('update:visible', false);
    },
  },
};
</script>

<style lang="scss" scoped>
.product-param-image-dialog {
  ::v-deep .el-dialog__body {
    max-height: 700px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
}

.table-container {
  flex: 1;
  overflow: hidden;
}

.config-table {
  width: 100%;
}

.image-cell-content {
  padding: 10px;
}

.image-error-tip {
  color: #f56c6c;
  font-size: 12px;
  line-height: 1;
  padding: 4px 0 0;
}

.pagination-wrapper {
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
}
</style>
